import {
  DynamicRetrievalMode,
  GoogleGenerativeAI,
  type GenerateContentRequest,
  type GenerationConfig,
  type Part,
  type Tool,
} from '@google/generative-ai'
import { firebaseAuth } from './firebase'
import { getModelSpec } from '../config/aiModelRegistry'

/**
 * Dual-pathway Gemini client.
 *
 * Path 1 (primary) — `@google/generative-ai` browser SDK with an AI Studio
 *   API key. Fast, no network hop beyond the model.
 * Path 2 (fallback) — POST to a Firebase Cloud Function proxy that calls
 *   Vertex AI server-side using Application Default Credentials (the JSON
 *   service account never touches the browser).
 *
 * Why a proxy: this is a Vite SPA — there is no Node runtime in the user's
 * browser. Vertex AI's ADC-based auth fundamentally requires a server, so the
 * fallback path *must* go through one. See the migration notes in your
 * .env.local for the proxy URL config.
 *
 * Behavior: every call tries Path 1 first; on ANY error (rate limit, billing
 * block, network) it logs a warning and tries Path 2. If both fail, the
 * original error chain re-throws so callers' existing error handling still
 * fires (e.g. `parseGeneratedJson`'s repair flow).
 */

const fallbackApiKey = import.meta.env.VITE_GEMINI_API_KEY as string | undefined
const vertexProxyUrl = import.meta.env.VITE_VERTEX_PROXY_URL as string | undefined

const localClient = fallbackApiKey ? new GoogleGenerativeAI(fallbackApiKey) : null

/**
 * Pathway switch. Resolved once at module load from `VITE_GEMINI_MODE`:
 *   - 'auto'    → AI Studio first, fall back to Vertex on error (default).
 *   - 'studio'  → AI Studio only; if it fails the error propagates.
 *   - 'vertex'  → Skip AI Studio entirely, go straight to the Vertex proxy.
 *
 * Anything else (unset, typo, random string) collapses to 'auto'. Switching
 * modes requires a Vite restart — Vite injects env vars at build time.
 */
type GeminiMode = 'auto' | 'studio' | 'vertex'
const resolveMode = (raw: string | undefined): GeminiMode => {
  if (raw === 'studio' || raw === 'vertex' || raw === 'auto') return raw
  return 'auto'
}
const geminiMode: GeminiMode = resolveMode(import.meta.env.VITE_GEMINI_MODE as string | undefined)

/**
 * Models that AI Studio (generativelanguage.googleapis.com) no longer serves
 * to new API keys. Calling them there returns a 404 with a "model no longer
 * available" body — the auto-fallback to Vertex still recovers, but we save
 * the round-trip and the noisy console warning by short-circuiting Studio
 * entirely for these ids.
 *
 * Add ids here when Google retires them from Studio. Keep in sync with the
 * `aiModelRegistry.json` catalogue — the registry entries are still valid on
 * Vertex, only Studio is dark.
 */
const STUDIO_UNSUPPORTED_MODELS: ReadonlySet<string> = new Set([
  'gemini-2.5-pro',
])

/** Any of the legacy SDK's `generateContent` overload-accepted payload shapes. */
export type GeminiPayload =
  | string
  | Array<string | Part>
  | GenerateContentRequest

export interface GeminiGenerateRequest {
  model: string
  generationConfig?: GenerationConfig
  payload: GeminiPayload
  useGoogleSearch?: boolean
  /**
   * Optional abort signal. The Vertex proxy path honors this at the network
   * level (true cancellation). The AI Studio SDK path cannot be cancelled
   * mid-request, but if the signal fires before/between attempts the call
   * throws immediately.
   */
  signal?: AbortSignal
}

export interface GeminiGenerateResult {
  text: string
  /** Which pathway served the response. Useful for telemetry. */
  source: 'studio' | 'vertex'
  usageMetadata?: GeminiUsageMetadata
}

export interface GeminiUsageMetadata {
  promptTokenCount?: number
  candidatesTokenCount?: number
  totalTokenCount?: number
  thoughtsTokenCount?: number
}

const normalizeUsageMetadata = (value: unknown): GeminiUsageMetadata | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  const usage: GeminiUsageMetadata = {}
  const copyNumber = (from: string, to: keyof GeminiUsageMetadata = from as keyof GeminiUsageMetadata) => {
    const raw = record[from]
    if (typeof raw === 'number' && Number.isFinite(raw)) usage[to] = raw
  }
  copyNumber('promptTokenCount')
  copyNumber('candidatesTokenCount')
  copyNumber('totalTokenCount')
  copyNumber('thoughtsTokenCount')
  return Object.keys(usage).length > 0 ? usage : undefined
}

const GOOGLE_SEARCH_TOOLS: Tool[] = [
  {
    googleSearchRetrieval: {
      dynamicRetrievalConfig: {
        mode: DynamicRetrievalMode.MODE_DYNAMIC,
        dynamicThreshold: 0.35,
      },
    },
  },
]

const throwIfAborted = (signal: AbortSignal | undefined) => {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError')
  }
}

const callStudio = async (request: GeminiGenerateRequest): Promise<{ text: string; usageMetadata?: GeminiUsageMetadata }> => {
  if (!localClient) throw new Error('AI Studio client not configured (VITE_GEMINI_API_KEY missing).')
  throwIfAborted(request.signal)
  const model = localClient.getGenerativeModel({
    model: request.model,
    generationConfig: request.generationConfig,
    tools: request.useGoogleSearch ? GOOGLE_SEARCH_TOOLS : undefined,
  })
  // The legacy SDK overloads `generateContent` for string / Part[] /
  // GenerateContentRequest; the wrapper accepts the same union and forwards.
  // The SDK doesn't expose an abort hook, so the signal can only short-circuit
  // before invocation. Re-check on return so an abort that fired during the
  // call still propagates to the caller.
  const result = await model.generateContent(request.payload as Parameters<typeof model.generateContent>[0])
  throwIfAborted(request.signal)
  return {
    text: result.response.text(),
    usageMetadata: normalizeUsageMetadata((result.response as { usageMetadata?: unknown }).usageMetadata),
  }
}

const callVertexProxy = async (request: GeminiGenerateRequest): Promise<{ text: string; usageMetadata?: GeminiUsageMetadata }> => {
  if (!vertexProxyUrl) {
    throw new Error('Vertex proxy not configured (VITE_VERTEX_PROXY_URL missing).')
  }
  throwIfAborted(request.signal)
  // Attach the caller's Firebase ID token so the proxy can gate anonymous
  // requests before spending on Vertex. Falls back to no header when the
  // user is anonymous — the proxy will reject those with 401.
  const user = firebaseAuth.currentUser
  const idToken = user ? await user.getIdToken() : null
  // Send the model's registry-declared Vertex region so the proxy can honor
  // per-model routing without a redeploy every time a model moves regions.
  // Falls back to the proxy's own default when the model has no registry entry.
  const vertexLocation = getModelSpec(request.model)?.vertexLocation
  const res = await fetch(vertexProxyUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({
      model: request.model,
      generationConfig: request.generationConfig,
      payload: request.payload,
      useGoogleSearch: request.useGoogleSearch,
      ...(vertexLocation ? { location: vertexLocation } : {}),
    }),
    signal: request.signal,
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`Vertex proxy responded ${res.status}: ${detail || 'no body'}`)
  }
  const data = (await res.json()) as { text?: string; error?: string; usageMetadata?: unknown }
  if (data.error) throw new Error(`Vertex proxy error: ${data.error}`)
  if (typeof data.text !== 'string') throw new Error('Vertex proxy returned no text payload.')
  return { text: data.text, usageMetadata: normalizeUsageMetadata(data.usageMetadata) }
}

const callStudioWithSearchRetry = async (
  request: GeminiGenerateRequest,
): Promise<{ text: string; usageMetadata?: GeminiUsageMetadata }> => {
  try {
    return await callStudio(request)
  } catch (error) {
    if (!request.useGoogleSearch) throw error
    return callStudio({ ...request, useGoogleSearch: false })
  }
}

const callVertexWithSearchRetry = async (
  request: GeminiGenerateRequest,
): Promise<{ text: string; usageMetadata?: GeminiUsageMetadata }> => {
  try {
    return await callVertexProxy(request)
  } catch (error) {
    if (!request.useGoogleSearch) throw error
    return callVertexProxy({ ...request, useGoogleSearch: false })
  }
}

/**
 * Dispatcher that honors `VITE_GEMINI_MODE`:
 *   - 'studio' → only AI Studio (error propagates if it fails).
 *   - 'vertex' → only Vertex proxy.
 *   - 'auto'   → AI Studio first, fall back to Vertex on any error.
 *
 * The 'auto' fallback path matches the original behavior. Callers don't
 * need to care which mode is active — they always get back `{ text, source }`.
 *
 * Exception: Claude models (Line Pro / Line Max) NEVER go through AI Studio
 * — Studio only serves Gemini. Claude requests always go straight to the
 * Vertex proxy, which dispatches to the Anthropic-on-Vertex endpoint.
 */
const isClaudeModel = (model: string): boolean => model.startsWith('claude-')

export const generateContentWithFallback = async (
  request: GeminiGenerateRequest,
): Promise<GeminiGenerateResult> => {
  // Claude → always Vertex proxy. AI Studio has no Claude models.
  if (isClaudeModel(request.model)) {
    if (!vertexProxyUrl) {
      throw new Error('Vertex proxy not configured (VITE_VERTEX_PROXY_URL missing) — cannot dispatch Claude tier calls.')
    }
    // Claude never uses Google Search grounding — force it off to avoid the
    // proxy passing an unsupported tool hint downstream.
    const result = await callVertexProxy({ ...request, useGoogleSearch: false })
    return { ...result, source: 'vertex' }
  }

  // Studio-dark Gemini ids (see STUDIO_UNSUPPORTED_MODELS) → Vertex directly.
  // Skips a guaranteed 404 on AI Studio + the warning it logs.
  if (STUDIO_UNSUPPORTED_MODELS.has(request.model)) {
    if (!vertexProxyUrl) {
      throw new Error(`Model "${request.model}" is not available on AI Studio and no Vertex proxy is configured.`)
    }
    const result = await callVertexWithSearchRetry(request)
    return { ...result, source: 'vertex' }
  }

  // Studio-only: no fallback. If the key is missing we throw before calling.
  if (geminiMode === 'studio') {
    if (!localClient) {
      throw new Error('VITE_GEMINI_MODE=studio but VITE_GEMINI_API_KEY is not configured.')
    }
    const result = await callStudioWithSearchRetry(request)
    return { ...result, source: 'studio' }
  }

  // Vertex-only: skip Studio entirely.
  if (geminiMode === 'vertex') {
    if (!vertexProxyUrl) {
      throw new Error('VITE_GEMINI_MODE=vertex but VITE_VERTEX_PROXY_URL is not configured.')
    }
    const result = await callVertexWithSearchRetry(request)
    return { ...result, source: 'vertex' }
  }

  // Auto: Studio first, Vertex fallback. Both failures re-throw so callers
  // can preserve their own retry/repair logic.
  let studioError: unknown = null
  if (localClient) {
    try {
      const result = await callStudioWithSearchRetry(request)
      return { ...result, source: 'studio' }
    } catch (error) {
      studioError = error
      console.warn('[gemini] AI Studio pathway failed, trying Vertex proxy', error)
    }
  }
  try {
    const result = await callVertexWithSearchRetry(request)
    return { ...result, source: 'vertex' }
  } catch (vertexError) {
    console.error('[gemini] Both AI Studio and Vertex pathways failed', { studioError, vertexError })
    throw vertexError instanceof Error
      ? vertexError
      : new Error('AI services unavailable: both AI Studio and Vertex proxy failed.')
  }
}

/**
 * True iff the configured mode has a usable pathway. UI gates AI affordances
 * on this — e.g., disabling the AI Magic button when neither path is wired.
 */
export const isGeminiConfigured = (): boolean => {
  if (geminiMode === 'studio') return localClient !== null
  if (geminiMode === 'vertex') return Boolean(vertexProxyUrl)
  return localClient !== null || Boolean(vertexProxyUrl)
}
